import { Component } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { parse } from 'date-fns';
import { DateUtilService } from 'gung-common';
import {
  AuthService,
  Availability,
  AvailabilityService,
  CartRow,
  CartRowPrice,
  CheckoutObject,
  PriceService,
  Product,
  ProductService,
  SelectedCustomerService
} from 'gung-standard';
import { DeliveryLocationsJeevesComponent, DeliveryLocationsService } from 'gung-standard-jeeves';
import { first, map } from 'rxjs';
import { CarLinerMysetupService } from '../../services/car-liner-mysetup.service';

@Component({
    selector: 'car-liner-delivery-locations-jeeves',
    templateUrl: './car-liner-delivery-locations-jeeves.component.html',
    styleUrls: ['./car-liner-delivery-locations-jeeves.component.scss'],
    standalone: false
})
export class CarLinerDeliveryLocationsJeevesComponent extends DeliveryLocationsJeevesComponent {
  productsMap?: { [productId: string]: Product };
  pricesMap?: { [productId: string]: CartRowPrice };

  constructor(
    protected selectedCustomerService: SelectedCustomerService,
    protected deliveryLocationsService: DeliveryLocationsService,
    protected formBuilder: FormBuilder,
    protected authService: AuthService,
    protected productService: ProductService,
    protected priceService: PriceService,
    public dateUtilService: DateUtilService,
    protected availabilityService: AvailabilityService,
    protected carLinerMysetupService: CarLinerMysetupService
  ) {
    super(selectedCustomerService, deliveryLocationsService, formBuilder, authService);
  }

  ngOnInit() {
    super.ngOnInit();
    const ids = this.checkout.rows.map(row => row.id);
    this.mapProducts(ids);
    const cartRows: CartRow[] = this.checkout.rows.map(checkoutRow => {
      const rowMap: CartRow = {
        extra: checkoutRow.extra,
        qty: checkoutRow.quantity,
        productId: checkoutRow.id,
        productPartialId: checkoutRow.partialId,
        timeUpdated: new Date(),
        timeAdded: new Date()
      };
      return rowMap;
    });
    this.mapProductsPrice(cartRows);
    this.setOrderTermsJeeves();
  }

  protected initForm() {
    super.initForm();
    this.formOh.addControl('q_internal_notes', this.formBuilder.control(this.checkout.extra.oh.q_internal_notes || ''));
    if (this.formOh.contains('editext')) {
      this.formOh.removeControl('editext');
    }
  }

  public getBtnTexts(checkout: CheckoutObject): { next: string | void; prev: string | void } | void {
    return {
      next: 'SUBMIT_ORDER',
      prev: null
    };
  }

  protected mapProducts(ids: string[]) {
    this.productService.getProductsByIds(ids).subscribe(products => {
      this.productsMap = {};
      products.forEach(product => (this.productsMap[product.id] = product));
    });
  }

  protected mapProductsPrice(cartRows: CartRow[]) {
    this.priceService
      .getCartRowPricesObservable(cartRows)
      .pipe(first())
      .subscribe(priceElements => {
        this.pricesMap = {};
        priceElements.forEach(priceElement => (this.pricesMap[priceElement.productId] = priceElement));
      });
    this.carLinerMysetupService
      .getMySetup()
      .pipe(first())
      .subscribe(setup => {
        this.checkout.rows.map(r => {
          if (setup.markUp && !r.extra._userChangePrice) {
            r.extra.orp.vb_pris = parseFloat((r.extra.orp.vb_pris / (1 + setup.markUp / 100)).toFixed(2));
          }
          return r;
        });
      });
  }

  setOrderTermsJeeves() {
    this.availabilityService
      .getAvailabilities(
        this.checkout.rows.map(r => r.id),
        undefined,
        true
      )
      .pipe(map(avs => avs.map(av => ({ ...av, availabilities: av.extra.availabilities }))))
      .subscribe(avs => {
        let minDate = new Date();
        for (const row of this.checkout.rows) {
          if (row.extra.orp.ordberlevdat) {
            const tempDate = new Date(row.extra.orp.ordberlevdat);
            if (minDate < tempDate) {
              minDate = tempDate;
            }
          } else {
            const av = avs.find(a => a.productId === row.id);
            if (av) {
              const tempDate = getMinDate(av);
              if (minDate < tempDate) {
                minDate = tempDate;
              }
            }
          }
        }
      });
  }

  protected handleNextButtonClicked(): void {
    if (this.formOh.invalid) {
      this.formOh.markAllAsTouched();
      return;
    }
    if (this.form.invalid) {
      return;
    }

    this.checkout.rows.forEach(row => {
      delete row.extra.orp.rabatt1;
      delete row.extra.orp.vb_pris;
    });

    this.assignPostalCodeAndCity(this.postalCode, this.city);
    this.assignFormValues(this.form, this.formOh);
    this.stepDone.emit(this.checkout);
  }
}

const getMinDate = (av: Availability): Date => {
  if (av.currentAvailability > 0) {
    return new Date();
  }
  if (av.maxFutureStock === 0) {
    return new Date();
  }
  const valids = Object.keys(av.availabilities).filter(entry => av.availabilities[entry] > 0);
  const dateFormat = 'yyMMdd';
  return valids.length > 0 ? parse(valids[0], dateFormat, new Date()) : new Date();
};
